import { ElementRef, Injectable, OnDestroy } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class ContainerHeightService implements OnDestroy {
  private activeObservers = new Map<string, {
    timeout: any;
    lastHeight: number;
    elementRef: ElementRef;
    componentId: string;
    callback: (componentId: string, height: number) => void;
    isReporting: boolean;
    resizeObserver?: ResizeObserver;
    additionalData?: any;
  }>();

  ngOnDestroy() {
    this.cleanup();
  }

  /**
   * Standard height change handler for iframe communication
   * Use this as the callback for components that need to report height to parent iframe
   */
  static onHeightChanged(componentId: string, height: number, additionalData?: any): void {
    if (window.parent && window.parent !== window) {
      const message: any = {
        type: 'height-change',
        componentId,
        currentHeight: height
      };

      // Include any additional data (like primaryColor)
      if (additionalData) {
        Object.assign(message, additionalData);
      }

      console.log(`** ${componentId} height * ${height}px`, additionalData ? additionalData : '');
      
      window.parent.postMessage(message, '*');
    }
  }

  startHeightReporting(
    componentId: string,
    elementRef: ElementRef,
    callback: (componentId: string, height: number) => void,
    debounceMs: number = 100,
    useResizeObserver: boolean = true,
    additionalData?: any
  ): void {
    if (this.activeObservers.has(componentId)) {
      this.stopHeightReporting(componentId);
    }

    const observer = {
      timeout: null,
      lastHeight: 0,
      elementRef,
      componentId,
      callback,
      isReporting: false,
      resizeObserver: undefined,
      additionalData
    };

    this.activeObservers.set(componentId, observer);

    // Set up ResizeObserver if supported and requested
    if (useResizeObserver && typeof ResizeObserver !== 'undefined' && elementRef?.nativeElement) {
      observer.resizeObserver = new ResizeObserver(() => {
        this.checkAndReportHeight(componentId);
      });
      observer.resizeObserver.observe(elementRef.nativeElement);
    }

    // Also do initial height check
    this.scheduleHeightCheck(componentId, debounceMs);
  }

  /**
   * Convenience method for components that need standard iframe height reporting
   */
  startIframeHeightReporting(
    componentId: string,
    elementRef: ElementRef,
    debounceMs: number = 100
  ): void {
    this.startHeightReporting(
      componentId,
      elementRef,
      (id: string, height: number) => ContainerHeightService.onHeightChanged(id, height),
      debounceMs,
      true
    );
  }

  stopHeightReporting(componentId: string): void {
    const observer = this.activeObservers.get(componentId);
    if (observer) {
      if (observer.timeout) {
        clearTimeout(observer.timeout);
      }
      if (observer.resizeObserver) {
        observer.resizeObserver.disconnect();
      }
      this.activeObservers.delete(componentId);
    }
  }

  triggerHeightCheck(componentId: string, debounceMs: number = 100): void {
    if (this.activeObservers.has(componentId)) {
      this.scheduleHeightCheck(componentId, debounceMs);
    }
  }

  private scheduleHeightCheck(componentId: string, debounceMs: number): void {
    const observer = this.activeObservers.get(componentId);
    if (!observer) return;

    if (observer.timeout) {
      clearTimeout(observer.timeout);
    }

    observer.timeout = setTimeout(() => {
      this.checkAndReportHeight(componentId);
    }, debounceMs);
  }

  private checkAndReportHeight(componentId: string): void {
    const observer = this.activeObservers.get(componentId);
    if (!observer || !observer.elementRef?.nativeElement) return;

    // Prevent multiple simultaneous height checks
    if (observer.isReporting) return;

    try {
      observer.isReporting = true;
      const element = observer.elementRef.nativeElement;
      const height = Math.ceil(element.getBoundingClientRect().height);
      
      // Only report if height has changed significantly (more than 1px)
      if (height && Math.abs(height - observer.lastHeight) > 1) {
        console.log(`Height changed for ${componentId}:`, height, 'Previous:', observer.lastHeight);
        observer.lastHeight = height;
        observer.callback(componentId, height);
      }
    } finally {
      observer.isReporting = false;
    }
  }

  private cleanup(): void {
    for (const [componentId] of this.activeObservers) {
      this.stopHeightReporting(componentId);
    }
  }
} 